import { getEntityEffectiveEntityName } from './entity';
import { EntityNameEnum, LookupEntityTypeEnum } from '@ppl/graphql-space-api';
import type {
  AccountEntity,
  AppointmentEntity,
  ClientEntity,
  ContactEntity,
  CustomEntityEntity,
  FieldDescriptor,
  LeadEntity,
  OpportunityEntity,
  ProductEntity,
  ProjectEntity,
  QuoteEntity,
  TaskEntity
} from '@ppl/graphql-space-api';
import type { EntityType } from './entity';


export type LookupEntity =
  LeadEntity |
  OpportunityEntity |
  AccountEntity |
  ContactEntity |
  TaskEntity |
  AppointmentEntity |
  ProductEntity |
  ClientEntity |
  ProjectEntity |
  QuoteEntity |
  CustomEntityEntity;

export const lookupEntityTypeToEntityNameMap = {
  [LookupEntityTypeEnum.Account]: EntityNameEnum.Account,
  [LookupEntityTypeEnum.Appointment]: EntityNameEnum.Appointment,
  [LookupEntityTypeEnum.Client]: EntityNameEnum.Client,
  [LookupEntityTypeEnum.Contact]: EntityNameEnum.Contact,
  [LookupEntityTypeEnum.CustomEntity]: EntityNameEnum.CustomEntity,
  [LookupEntityTypeEnum.Lead]: EntityNameEnum.Lead,
  [LookupEntityTypeEnum.Opportunity]: EntityNameEnum.Opportunity,
  [LookupEntityTypeEnum.OpptyProductRelation]: EntityNameEnum.OpptyProductRelation,
  [LookupEntityTypeEnum.Product]: EntityNameEnum.Product,
  [LookupEntityTypeEnum.Project]: EntityNameEnum.Project,
  [LookupEntityTypeEnum.Quote]: EntityNameEnum.Quote,
  [LookupEntityTypeEnum.Task]: EntityNameEnum.Task,
} as const;

type LookupEntityType = keyof typeof lookupEntityTypeToEntityNameMap;
export type LookupEntityName = typeof lookupEntityTypeToEntityNameMap[LookupEntityType];

export function getEntityLookupFields(
  data: {
    fieldDescriptors: { [entityName: string]: FieldDescriptor[]; };
    sourceEntityType: EntityType;
  },
  options: {
    includeDeletedLookupFields?: boolean;
    filterLookupEntityFieldsFn?: (field: FieldDescriptor) => boolean;
  } = {}
): {
  sourceEntity: EntityType;
  lookupFields: LookupField[]
} {
  const sourceEntityEffectiveEntityName = getEntityEffectiveEntityName(data.sourceEntityType);
  const sourceEntityLookupFields = (data.fieldDescriptors[sourceEntityEffectiveEntityName] || []).filter(it => it.typeId === 'lookup');
  const lookupFields: LookupField[] = [];

  for (const lookupField of sourceEntityLookupFields) {
    if (options.includeDeletedLookupFields || !lookupField.isDeleted) {
      const lookupEntityType = {
        entityName: lookupEntityTypeToEntityNameMap[lookupField.lookup.entityType],
        entityApiName: lookupField.lookup.entityApiName
      };
      const lookupEntityFields = data.fieldDescriptors[getEntityEffectiveEntityName(lookupEntityType)];
      lookupFields.push({
        field: lookupField,
        lookupEntityType,
        lookupEntityFields: options.filterLookupEntityFieldsFn
          ? lookupEntityFields.filter(options.filterLookupEntityFieldsFn)
          : lookupEntityFields
      });
    }
  }

  return {
    sourceEntity: data.sourceEntityType,
    lookupFields
  };
}

export interface LookupField {
  field: FieldDescriptor;
  lookupEntityType: {
    entityName: LookupEntityName;
    entityApiName: string | null;
  };
  lookupEntityFields: FieldDescriptor[];
}
